/**
 * VendorSystemSettingsService
 * Service for managing vendor system settings
 */
import { CrudService } from '../base/CrudService';
/**
 * Service for vendor system setting CRUD operations with key/category/module queries
 */
export class VendorSystemSettingsService extends CrudService {
    constructor(http, config) {
        super(http, {
            servicePath: config?.servicePath ?? VendorSystemSettingsService.DEFAULT_SERVICE_PATH,
            basePath: VendorSystemSettingsService.BASE_PATH,
            endpoints: config?.endpoints,
        });
    }
    /**
     * Get a system setting by its key
     * @param key - The setting key
     * @returns The vendor system setting
     */
    async getByKey(key) {
        const response = await this.getByKeyRaw(key);
        if (!response.data) {
            throw new Error(`VendorSystemSetting not found: key=${key}`);
        }
        return response.data;
    }
    /**
     * Get a system setting by its key with full response
     * @param key - The setting key
     * @returns ResponseData with the vendor system setting
     */
    getByKeyRaw(key) {
        const url = this.buildUrl(`key/${key}`);
        return this.http.get(url);
    }
    /**
     * Get all system settings for a specific category
     * @param category - The category name
     * @returns Array of vendor system settings
     */
    async getByCategory(category) {
        const response = await this.getByCategoryRaw(category);
        return response.data ?? [];
    }
    /**
     * Get all system settings for a specific category with full response
     * @param category - The category name
     * @returns ResponseData with array of vendor system settings
     */
    getByCategoryRaw(category) {
        const url = this.buildUrl(`category/${category}`);
        return this.http.get(url);
    }
    /**
     * Get all system settings for a specific module
     * @param module - The module name
     * @returns Array of vendor system settings
     */
    async getByModule(module) {
        const response = await this.getByModuleRaw(module);
        return response.data ?? [];
    }
    /**
     * Get all system settings for a specific module with full response
     * @param module - The module name
     * @returns ResponseData with array of vendor system settings
     */
    getByModuleRaw(module) {
        const url = this.buildUrl(`module/${module}`);
        return this.http.get(url);
    }
    /**
     * Create or update a system setting by key
     * @param key - The setting key
     * @param data - The setting data
     * @returns The upserted vendor system setting
     */
    async upsertByKey(key, data) {
        const response = await this.upsertByKeyRaw(key, data);
        if (!response.data) {
            throw new Error(`Failed to upsert VendorSystemSetting: key=${key}`);
        }
        return response.data;
    }
    /**
     * Create or update a system setting by key with full response
     * @param key - The setting key
     * @param data - The setting data
     * @returns ResponseData with the upserted vendor system setting
     */
    upsertByKeyRaw(key, data) {
        const url = this.buildUrl(`key/${key}`);
        return this.http.put(url, data);
    }
}
VendorSystemSettingsService.DEFAULT_SERVICE_PATH = 'system';
VendorSystemSettingsService.BASE_PATH = 'api/vendor-system-settings';
//# sourceMappingURL=VendorSystemSettingsService.js.map